/**
 * Hover zoom component that shows a magnified lens/excerpt above the slide
 */
class HoverZoom extends HTMLElement {
  /** @type {HTMLImageElement | null} */
  #image;
  /** @type {HTMLDivElement | null} */
  #lens;
  /** @type {HTMLImageElement | null} */
  #lensImage;

  connectedCallback() {
    this.#image = this.querySelector(".product-media__image");
    if (!this.#image) return;

    // Create lens element
    this.#lens = document.createElement("div");
    this.#lens.className = "hover-zoom__lens";
    this.#lensImage = document.createElement("img");
    this.#lensImage.className = "hover-zoom__lens-image";
    this.#lensImage.src = this.#image.src;
    this.#lensImage.alt = this.#image.alt || "";
    this.#lens.appendChild(this.#lensImage);
    document.body.appendChild(this.#lens);

    this.addEventListener("mousemove", this.#handleMouseMove);
    this.addEventListener("mouseleave", this.#handleMouseLeave);
  }

  disconnectedCallback() {
    this.removeEventListener("mousemove", this.#handleMouseMove);
    this.removeEventListener("mouseleave", this.#handleMouseLeave);
    if (this.#lens && this.#lens.parentNode) {
      this.#lens.parentNode.removeChild(this.#lens);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#image || !this.#lens) return;

    const rect = this.getBoundingClientRect();
    const imageRect = this.#image.getBoundingClientRect();

    // Calculate mouse position relative to the image container
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Show lens and position it over the image (in z-direction, layered on top)
    if (!this.#lens) return;

    this.#lens.style.display = "block";
    // Position lens centered on cursor, over the image
    this.#lens.style.left = `${event.clientX - this.#lens.offsetWidth / 2}px`;
    this.#lens.style.top = `${event.clientY - this.#lens.offsetHeight / 2}px`;

    // Calculate background position for the zoomed image
    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;

    // Use high-res image if available
    const highResSrc = this.#image.dataset.maxResolution || this.#image.src;
    if (this.#lensImage) {
      this.#lensImage.src = highResSrc;

      // Set background position to show the correct portion
      const zoomLevel = 3;
      const lensWidth = this.#lens.offsetWidth || 300;
      const lensHeight = this.#lens.offsetHeight || 300;
      const bgX =
        (percentX / 100) * imageRect.width - lensWidth / zoomLevel / 2;
      const bgY =
        (percentY / 100) * imageRect.height - lensHeight / zoomLevel / 2;

      this.#lensImage.style.transform = `scale(${zoomLevel})`;
      this.#lensImage.style.transformOrigin = `${percentX}% ${percentY}%`;
    }
  };

  #handleMouseLeave = () => {
    if (this.#lens) {
      this.#lens.style.display = "none";
    }
  };
}

if (!customElements.get("hover-zoom")) {
  customElements.define("hover-zoom", HoverZoom);
}
